/**
 * This is the Admin Console component that allows FashionNova admin to manage
 * users accounts
 */
import { chain, assign, debounce } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import Header from '../Header/index.ts';
import Page from '../Page/index.ts';
import VendorsDataGrid from './VendorDataGrid/VendorsDataGrid.tsx';
import { FilterSearch } from '../FilterSearch/FilterSearch.tsx';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';

/**
 * The Admin console component
 */
class AdminConsole extends Component {
  constructor(props) {
    super(props);

    const { location, fetchUsers } = this.props;
    /**
     * The initial state of the component, with the modal closed, and no vendor
     * to edit currently
     */
    const queryParams = queryString.parse(props.location.search.slice(1));

    const appStatusMap = {
      Approved: 'APPROVED',
      'Application Pending': 'APPLICATION_PENDING',
      'Pending Accounting Approval': 'PENDING_ACCOUNTING_APPROVAL',
      Rejected: 'REJECTED',
    };

    const vendorTypeMap = {
      new: 'New',
      existing: 'Existing',
      'new-direct': 'New-Direct',
      'new-3rdparty': 'New-3rd Party',
      'new-hybrid': 'New-Hybrid',
      'existing-direct': 'Existing-Direct',
      'existing-3rdparty': 'Existing-3rd Party',
      'existing-hybrid': 'Existing-Hybrid',
    };

    const applicationStatus =
      appStatusMap[queryParams.applicationStatus] || 'all';
    const vendorType = vendorTypeMap[queryParams.vendorType] || 'all';
    const netsuiteStatus = queryParams.netsuiteStatus || 'all';

    this.state = {
      openMessageModal: false,
      applicationStatus,
      vendorType,
      netsuiteStatus,
      appliedFilters: {
        applicationStatus,
        vendorType,
        netsuiteStatus,
      },
    };

    this.updateUrl = this.updateUrl.bind(this);
    this.removeSortParams = this.removeSortParams.bind(this);
    this.updateUrlHeader = this.updateUrlHeader.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.toggleMessageModal = this.toggleMessageModal.bind(this);
    this.handleApplicationStatusFilter =
      this.handleApplicationStatusFilter.bind(this);
    this.handleVendorTypeFilter = this.handleVendorTypeFilter.bind(this);
    this.handleNetsuiteStatusFilter =
      this.handleNetsuiteStatusFilter.bind(this);

    fetchUsers(location.search.slice(1));
  }

  componentDidUpdate(prevProps, prevState) {
    // When loading transitions from true to false, update appliedFilters
    if (prevProps.loading && !this.props.loading) {
      const { applicationStatus, vendorType, netsuiteStatus } = this.state;
      this.setState({
        appliedFilters: {
          applicationStatus,
          vendorType,
          netsuiteStatus,
        },
      });
    }
  }

  /**
   * Adds query parameters to the url to update the query to our api and calls
   * fetchUsers
   *
   * @param {string} name the name of the param to be added to the url
   * @param {string} value the value being added to the url
   *
   * @returns undefined
   */
  updateUrl(name, value) {
    const {
      history,
      location: { search: params, pathname },
    } = this.props;
    const { fetchUsers } = this.props;
    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ [name]: value })
      .thru((p) =>
        name === 'search' ? assign(p, { page: 1, role: 'vendor' }) : p,
      )
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });
    fetchUsers(search);
  }

  /**
   * A function that updates the url when different parameters in the component
   * are updated. Calls fetchUsers to get all users that
   * match the params. Called when sort is altered using table headers
   * @param {string} sortBy the name of the parameter to be added to the url
   * @param {string} sortOrder the order parameter (ex. 'ASC'/'DESC')
   */
  updateUrlHeader(e, field) {
    const {
      fetchUsers,
      history,
      location: { search: params, pathname },
    } = this.props;

    const sortParams = decodeLocation(params);
    const { sortBy, sortOrder } = sortParams;

    let newSortOrder;
    if (!sortOrder || field !== sortBy) {
      newSortOrder = sortOrder || 'ASC';
    } else {
      newSortOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
    }

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: field, sortOrder: newSortOrder })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    fetchUsers(search);
  }

  removeSortParams() {
    const {
      fetchUsers,
      history,
      location: { search: params, pathname },
    } = this.props;
    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: undefined, sortOrder: undefined })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    fetchUsers(search);
  }

  /**
   * Updates the url to increment, decrement, or navigate directly to a
   * specific results page.
   *
   * @param {number} page
   */
  handleChangePage(page) {
    this.updateUrl('page', page);
  }

  toggleMessageModal() {
    const { openMessageModal } = this.state;
    this.setState({ openMessageModal: !openMessageModal });
  }

  handleApplicationStatusFilter(value) {
    this.setState({ applicationStatus: value });
    if (value === 'all') {
      this.updateUrl('applicationStatus', '');
    } else {
      const statusMap = {
        [ApplicationStatus.APPROVED]: 'Approved',
        [ApplicationStatus.APPLICATION_PENDING]: 'Application Pending',
        [ApplicationStatus.PENDING_ACCOUNTING_APPROVAL]:
          'Pending Accounting Approval',
        [ApplicationStatus.REJECTED]: 'Rejected',
      };
      const {
        history,
        location: { search: params, pathname },
      } = this.props;
      const { fetchUsers } = this.props;
      const search = chain(params.slice(1))
        .thru(queryString.parse)
        .assign({
          applicationStatus: statusMap[value] || value,
          page: 1,
        })
        .pickBy()
        .thru(queryString.encode)
        .value();

      history.push({ pathname, search });
      fetchUsers(search);
    }
  }

  handleVendorTypeFilter(value) {
    this.setState({ vendorType: value });
    if (value === 'all') {
      this.updateUrl('vendorType', '');
    } else {
      const {
        history,
        location: { search: params, pathname },
      } = this.props;
      const { fetchUsers } = this.props;

      let searchParams = {};

      if (value === 'New') {
        searchParams = { vendorType: 'new', page: 1 };
      } else if (value === 'Existing') {
        searchParams = { vendorType: 'existing', page: 1 };
      } else {
        const vendorTypeMap = {
          'New-Direct': 'new-direct',
          'New-3rd Party': 'new-3rdparty',
          'New-Hybrid': 'new-hybrid',
          'Existing-Direct': 'existing-direct',
          'Existing-3rd Party': 'existing-3rdparty',
          'Existing-Hybrid': 'existing-hybrid',
        };
        searchParams = { vendorType: vendorTypeMap[value], page: 1 };
      }
      const existingParams = queryString.parse(params.slice(1));

      delete existingParams.existing_vendor;
      delete existingParams.vendorType;

      const finalParams = { ...existingParams, ...searchParams };

      const search = chain(finalParams)
        .pickBy()
        .thru(queryString.encode)
        .value();

      history.push({ pathname, search });
      fetchUsers(search);
    }
  }

  handleNetsuiteStatusFilter(value) {
    this.setState({ netsuiteStatus: value });
    if (value === 'all') {
      this.updateUrl('netsuiteStatus', '');
    } else {
      const netsuiteStatusMap = {
        active: 'active',
        inactive: 'inactive',
      };
      const {
        history,
        location: { search: params, pathname },
      } = this.props;
      const { fetchUsers } = this.props;
      const search = chain(params.slice(1))
        .thru(queryString.parse)
        .assign({
          netsuiteStatus: netsuiteStatusMap[value] || value,
          page: 1,
        })
        .pickBy()
        .thru(queryString.encode)
        .value();

      history.push({ pathname, search });
      fetchUsers(search);
    }
  }

  render() {
    const { applicationStatus, vendorType, netsuiteStatus, appliedFilters } =
      this.state;
    const { location, users, count, loading } = this.props;
    const params = decodeLocation(location.search);
    const seachValue = queryString.parse(location.search.slice(1)).search || '';

    const applicationStatusOptions = [
      { value: 'all', label: 'All' },
      { value: 'APPROVED', label: 'Approved' },
      { value: 'APPLICATION_PENDING', label: 'Application Pending' },
      {
        value: 'PENDING_ACCOUNTING_APPROVAL',
        label: 'Pending Accounting Approval',
      },
      { value: 'REJECTED', label: 'Rejected' },
    ];

    const vendorTypeOptions = [
      { value: 'all', label: 'All' },
      { value: 'New', label: 'New' },
      {
        value: 'New-Direct',
        label: 'New - Direct Factory / Manufacturer',
      },
      {
        value: 'New-3rd Party',
        label: 'New - Importer / Wholesaler',
      },
      {
        value: 'New-Hybrid',
        label: 'New - Agent',
      },
      { value: 'Existing', label: 'Existing' },
      {
        value: 'Existing-Direct',
        label: 'Existing - Direct Factory / Manufacturer',
      },
      {
        value: 'Existing-3rd Party',
        label: 'Existing - Importer / Wholesaler',
      },
      {
        value: 'Existing-Hybrid',
        label: 'Existing - Agent',
      },
    ];

    const netsuiteStatusOptions = [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ];

    const showCount =
      !loading &&
      applicationStatus === appliedFilters.applicationStatus &&
      vendorType === appliedFilters.vendorType &&
      netsuiteStatus === appliedFilters.netsuiteStatus;

    return (
      <Page.Container>
        <Header.Major icon="building/building-apartment-two" title="Vendors" />
        <Header.Minor
          search={{
            value: seachValue,
            onChange: debounce((value) => this.updateUrl('search', value), 500),
          }}
        >
          <FilterSearch
            value={applicationStatus}
            items={applicationStatusOptions}
            label="Application Status"
            emptyLabel="Application Status"
            onAddValue={this.handleApplicationStatusFilter}
            onRemoveValue={() => this.handleApplicationStatusFilter('all')}
            resultCount={count}
            loading={loading}
            showCount={showCount}
          />
          <FilterSearch
            value={vendorType}
            items={vendorTypeOptions}
            label="Vendor Type"
            emptyLabel="Vendor Type"
            onAddValue={this.handleVendorTypeFilter}
            onRemoveValue={() => this.handleVendorTypeFilter('all')}
            resultCount={count}
            loading={loading}
            showCount={showCount}
          />
          <FilterSearch
            value={netsuiteStatus}
            items={netsuiteStatusOptions}
            label="Netsuite Status"
            emptyLabel="Netsuite Status"
            onAddValue={this.handleNetsuiteStatusFilter}
            onRemoveValue={() => this.handleNetsuiteStatusFilter('all')}
            resultCount={count}
            loading={loading}
            showCount={showCount}
          />
        </Header.Minor>
        <Page.Content>
          <VendorsDataGrid
            loading={loading}
            onSortModelChange={(model) => {
              if (model.length > 0) {
                this.updateUrlHeader(undefined, model[0].field);
              } else {
                this.removeSortParams();
              }
            }}
            currentUser={this.props.currentUser}
            sortModel={
              params?.sortBy
                ? [
                    {
                      field: params.sortBy,
                      sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                    },
                  ]
                : []
            }
            paginationModel={{
              page: params?.page ? params.page - 1 : 0,
              pageSize: 15,
            }}
            onPaginationModelChange={(model) => {
              this.updateUrl('page', model.page + 1);
            }}
            pageSizeOptions={[15]}
            rowCount={count}
            rows={users}
            applicationStatus={applicationStatus}
            vendorType={vendorType}
            uiVendorType={vendorType}
            netsuiteStatus={netsuiteStatus}
          />
        </Page.Content>
      </Page.Container>
    );
  }
}

AdminConsole.propTypes = {
  loading: PropTypes.bool.isRequired,
  location: locationProps.isRequired,
  logout: PropTypes.func.isRequired,
  history: historyProps.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string.isRequired,
      last_logged_in_at: PropTypes.string,
      vendor_name: PropTypes.string,
    }),
  ).isRequired,
  count: PropTypes.number.isRequired,
  fetchUsers: PropTypes.func.isRequired,
};

export default AdminConsole;
